import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { Home, Users, Dumbbell, BarChart3, User, BookOpen } from 'lucide-react';

// Маппинг иконок
const iconMap = {
  'fas fa-home': Home,
  'fas fa-users': Users,
  'fas fa-dumbbell': Dumbbell,
  'fas fa-chart-line': BarChart3,
  'fas fa-user': User,
  'fas fa-book': BookOpen,
};

/** Значение переменной, когда TabBar виден */
const TAB_BAR_HEIGHT_VALUE = 'calc(72px + env(safe-area-inset-bottom, 0px))';

/** После смены языка и др. — подольше глушим случайный тап по первой вкладке */
const TAB_SWITCH_SUPPRESS_MS = 1200;

const TabBar = ({ tabs, activeTab, onTabChange }) => {
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  /** После смены языка UI Radix/порталы иногда порождают ложный клик по первой вкладке — глушим короткое окно. */
  const suppressTabChangeUntilRef = useRef(0);

  useEffect(() => {
    const onLang = () => {
      suppressTabChangeUntilRef.current = Date.now() + TAB_SWITCH_SUPPRESS_MS;
    };
    window.addEventListener('mad:uiLang', onLang);
    return () => window.removeEventListener('mad:uiLang', onLang);
  }, []);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;

    const update = () => {
      const innerH = window.innerHeight;
      const ratio = innerH > 0 ? vv.height / innerH : 1;
      const isOpen = ratio < 0.82;
      setKeyboardOpen(isOpen);
      document.documentElement.style.setProperty(
        '--app-tab-bar-height',
        isOpen ? '0px' : TAB_BAR_HEIGHT_VALUE
      );
    };

    update();
    vv.addEventListener('resize', update, { passive: true });
    vv.addEventListener('scroll', update, { passive: true });
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      document.documentElement.style.setProperty('--app-tab-bar-height', TAB_BAR_HEIGHT_VALUE);
    };
  }, []);

  return createPortal(
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-[200] w-full max-w-[100vw] border-t border-border bg-background shadow-lg transition-[transform,opacity] duration-200 ease-out',
        keyboardOpen
          ? 'pointer-events-none translate-y-full opacity-0 border-transparent shadow-none'
          : 'pointer-events-auto'
      )}
      aria-hidden={keyboardOpen}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0)',
        paddingTop: '8px',
      }}
    >
      <div 
        className="mx-auto flex w-full min-w-0 max-w-screen-md items-center justify-around"
        style={{
          minHeight: '64px',
          paddingLeft: 'max(8px, env(safe-area-inset-left, 0px))',
          paddingRight: 'max(8px, env(safe-area-inset-right, 0px))',
          gap: '4px'
        }}
      >
        {tabs.map((tab) => {
          const IconComponent = iconMap[tab.icon] || Home;
          return (
            <Button
              key={tab.id}
              variant="ghost"
              className={cn(
                "flex items-center justify-center rounded-lg transition-all touch-manipulation active:scale-95",
                "flex-1 h-full py-2",
                activeTab === tab.id
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground active:text-foreground active:bg-accent"
              )}
              onClick={() => {
                if (Date.now() < suppressTabChangeUntilRef.current) return;
                onTabChange(tab.id);
              }}
              style={{ 
                minHeight: '56px',
                minWidth: '60px',
                maxHeight: '100%'
              }}
              title={tab.label}
            >
              <IconComponent className="h-6 w-6" />
            </Button>
          );
        })}
      </div>
    </div>,
    document.body
  );
};

export default TabBar;
